// @ts-nocheck
// Agent Portal (/cti2) — right-side notification tray, opened from the
// bell icon in Topbar.tsx. Ports the SAME notification backend/data shape
// src/components/layout/Header.tsx's bell + AgentNotificationPopup.tsx
// already use elsewhere in the app (API_NOTIFICATION_LIST scoped to
// 'assigned', API_NOTIFICATION_MARK_READ/MARK_ALL_READ, fields
// notification_type/description/created_at/is_read/is_deleted) — not a new
// endpoint. AgentPortalPage.tsx fetches on mount + 120s fallback poll and listens for the same `cti:notification_refresh` window
// event `useSocket.ts` already dispatches on the server's socket push (the
// exact pattern this app already used to port Announcements into cti2).
//
import { Bell, X, CheckCheck } from 'lucide-react';

function formatWhen(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}

export default function NotificationDrawer({ open, onClose, notifications = [], loading, onMarkRead, onMarkAllRead }) {
  const visible = notifications.filter((n) => !n?.is_deleted);
  const unreadCount = visible.filter((n) => !n?.is_read).length;

  return (
    <>
      <div
        className={`fixed inset-0 z-[1099] bg-black/20 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={`fixed right-0 top-0 z-[1100] flex h-full w-[360px] max-w-[92vw] flex-col border-l border-border bg-card shadow-2xl transition-transform duration-200 ${
          open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
        }`}
        aria-hidden={!open}
        aria-label="Notifications"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Bell className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">{unreadCount} new</span>
          )}
          <div className="ml-auto flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                title="Mark all as read"
                className="flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-medium text-primary hover:bg-primary/10"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              title="Close"
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2.5">
          {loading && visible.length === 0 ? (
            <div className="py-10 text-center text-xs text-muted-foreground">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 py-10 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/40" />
              <div className="text-xs text-muted-foreground">You're all caught up</div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {visible.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => !n.is_read && onMarkRead?.(n.id)}
                  className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    n.is_read
                      ? 'border-border/60 bg-transparent'
                      : 'border-primary/30 bg-primary/5 hover:bg-primary/10'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-foreground">{n.notification_type}</div>
                      {n.description && (
                        <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{n.description}</div>
                      )}
                      {n.created_at && (
                        <div className="mt-1 text-[9.5px] uppercase tracking-wide text-muted-foreground/70">
                          {formatWhen(n.created_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
